import {
  ColumnDef,
  ColumnPinningState,
  createColumnHelper,
} from "@tanstack/react-table";
import {
  CreatorBriefDataType,
  CreatorPlatformData,
  ManageCreatorData,
} from "../../stores/types/creatorsTypes.tsx";
import { useEffect, useMemo, useState } from "react";
import { fullFormatNumbers } from "../../helpers/functions/formatNumber.ts";
import { getPlatformIcon } from "../../helpers/functions/getPlatformIcon.tsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import campaignEmpty from "/images/empty_states/campaign-empty.svg";
import {
  RevActionsHeader,
  RevAvatar,
  RevButton,
  RevEmptyPage,
  RevLandingPagePreviewDialog,
  RevSpinner,
  RevStatusBadge,
  RevTooltip,
} from "@revmatics/catalyst";
import { manageCreatorsTabsData } from "./data.ts";
import {
  getFitColor,
  getFitNameByNumber,
} from "../../helpers/functions/getFitName.ts";
import { faDownload } from "@fortawesome/pro-solid-svg-icons";
import { faStars } from "@fortawesome/pro-light-svg-icons";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { CreateBriefDialog } from "../creatorBriefs/components/createBriefDialog/CreateBriefDialog.tsx";
import { useAppDispatch, useAppSelector } from "../../stores/hooks/hooks.ts";
import { setBriefForCreator } from "../../stores/slice/creatorsSlice.tsx";
import {
  deleteStatusCreator,
  downloadCreatorsList,
  fetchManageCreatorsList,
  interactionsCreator,
} from "../../stores/thunks/creatorsThunk.tsx";
import {
  RevBadge,
  RevLink,
  RevPopoverMenu,
  RevTable,
  RevTabs,
  RevActionButton,
} from "@revmatics/catalyst";

const columnHelper = createColumnHelper<ManageCreatorData>();

export const ManageCreatorsPage = () => {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState("creators_view");
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    right: ["actions"],
  });
  const [isLoadingDelete, setIsLoadingDelete] = useState<null | number>(null);
  const [isLoadingLiked, setIsLoadingLiked] = useState<null | number>(null);
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);

  const [isCreateBriefDialogOpen, setIsCreateBriefDialogOpen] = useState<
    null | number
  >(null);
  const manageCreators = useAppSelector(
    (state) => state.creators.manageCreators,
  );
  const isLoading = useAppSelector((state) => state.creators.isLoading);

  const [previewLink, setPreviewLink] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchManageCreatorsList());
  }, [dispatch]);

  const handleDownload = () => {
    setIsLoadingDownload(true);
    dispatch(
      downloadCreatorsList({
        interaction: activeTab === "creators_view" ? "liked" : "hidden",
      }),
    ).finally(() => setIsLoadingDownload(false));
  };

  const creatorsForTab = useMemo(() => {
    if (manageCreators && manageCreators.length > 0) {
      return manageCreators.reduce(
        (
          acc: {
            creators: ManageCreatorData[];
            hidden_creators: ManageCreatorData[];
          },
          item,
        ) => {
          if (item.user_interaction_status === "hidden") {
            acc.hidden_creators.push(item);
          } else {
            acc.creators.push(item);
          }
          return acc;
        },
        {
          creators: [],
          hidden_creators: [],
        },
      );
    }
  }, [manageCreators]);

  const displayCreators = useMemo(() => {
    if (creatorsForTab) {
      if (activeTab === "creators_view") {
        return creatorsForTab.creators;
      } else {
        return creatorsForTab.hidden_creators;
      }
    }
  }, [activeTab, creatorsForTab]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns = useMemo<ColumnDef<ManageCreatorData, any>[]>(
    () => [
      columnHelper.accessor("name", {
        id: "creatorName",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-sm text-monochrome-900">
            Creator
          </span>
        ),
        cell: (info) => {
          const originalData = info.row.original;
          return (
            <div className="flex gap-3 items-center">
              <RevAvatar
                variant="circle"
                size="medium"
                theme="light"
                image={
                  originalData.creator_image_link ||
                  originalData.creator_image_file ||
                  ""
                }
                firstName={originalData.name.split(" ")[0]}
                lastName={originalData.name.split(" ").pop()}
              />
              <RevLink
                pathname=""
                search={`creatorId=${originalData.id}&menu=true`}
                text={originalData.name}
                className="w-fit"
                data-testid="creator-row-link-name"
              />
            </div>
          );
        },
        enableSorting: true,
        size: 300,
      }),
      columnHelper.accessor("platforms", {
        id: "platform",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-sm text-monochrome-900">
            Platform
          </span>
        ),
        cell: (info) => {
          const platforms = info.getValue() as CreatorPlatformData[];
          const originalData = info.row.original;
          return (
            <div className="flex flex-col">
              {platforms.map(({ platform }, index) => {
                const icon = getPlatformIcon(platform.name.toLowerCase());
                return (
                  <div
                    key={`manage-list-icon-${originalData.id}-${platform.name}`}
                    className={`h-14 px-3 flex items-center ${index !== 0 ? "border-t border-monochrome-100" : ""}`}
                  >
                    {icon ? (
                      <FontAwesomeIcon
                        key={platform.id}
                        icon={icon}
                        className="text-monochrome-500 text-md"
                        title={platform.name}
                      />
                    ) : (
                      <span
                        key={platform.id}
                        className="text-xs text-monochrome-500"
                        title={platform.name}
                      >
                        {platform.name}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          );
        },
        enableSorting: true,
        size: 180,
      }),
      columnHelper.accessor("platforms", {
        id: "handle",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-sm text-monochrome-900">
            Handle
          </span>
        ),
        cell: (info) => {
          const platforms = info.getValue() as CreatorPlatformData[];
          const originalData = info.row.original;
          return (
            <div className="flex flex-col">
              {platforms.map((platform, index) => (
                <div
                  key={`manage-list-nickname-${originalData.id}-${platform.platform.name}`}
                  className={`h-14 px-3 flex items-center ${index !== 0 ? "border-t border-monochrome-100" : ""}`}
                >
                  {platform.nickname && (
                    <span className="whitespace-nowrap typography-body-sm text-monochrome-900">
                      @{platform.nickname}
                    </span>
                  )}
                </div>
              ))}
            </div>
          );
        },
        enableSorting: true,
        size: 180,
      }),
      columnHelper.accessor("creator_briefs", {
        id: "creatorBriefs",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-sm text-monochrome-900">
            Creator Brief Name
          </span>
        ),
        cell: (info) => {
          const originalData = info.row.original;
          const creatorBriefs = info.getValue() as CreatorBriefDataType[];
          const firstThreeElements = creatorBriefs.slice(0, 3);
          const lastElements = creatorBriefs.slice(3);

          if (creatorBriefs.length === 0) {
            return (
              <RevButton
                text="+ Generate Creator Brief"
                displayAs="button"
                buttonSize="extraSmall"
                variant="secondary"
                handleClick={() => setIsCreateBriefDialogOpen(originalData.id)}
                className="max-w-[174px]"
              />
            );
          }
          return (
            <div className="flex flex-col gap-1">
              {firstThreeElements.map((brief, index) => (
                <div
                  className={"flex items-center gap-1"}
                  key={`manage-creators-brief-first-${brief.id}`}
                >
                  <RevLink
                    pathname={`/creator-briefs/${brief.id}`}
                    text={brief.title}
                  />
                  {brief.is_ai_generated && (
                    <RevTooltip
                      position="top"
                      className="whitespace-normal w-[100px] xl:w-[200px]"
                      content="Revmatics automatically generated this Creator Brief from your data and other sources."
                    >
                      <FontAwesomeIcon
                        icon={faStars}
                        className="typography-body-md text-purple-600"
                      />
                    </RevTooltip>
                  )}
                  {index === 2 && lastElements.length > 0 && (
                    <RevPopoverMenu
                      btn={
                        <RevBadge
                          text={lastElements.length.toString()}
                          className={`!w-8 group-focus/button:bg-monochrome-200 group-hover/button:bg-monochrome-200`}
                        />
                      }
                      classNameBtn="w-full focus:outline-none"
                      className="shadow-elevation--1 md:shadow-elevation-2 rounded overflow-hidden z-20"
                    >
                      <div className="shadow-elevation-2 rounded-lg bg-white p-2 z-10 flex flex-col gap-1">
                        {lastElements.map((item) => (
                          <div
                            className={"flex items-center gap-1"}
                            key={`manage-creators-brief-last-${item.id}`}
                          >
                            <RevLink
                              pathname={`/creator-briefs/${item.id}`}
                              text={item.title}
                            />
                            {item.is_ai_generated && (
                              <RevTooltip
                                position="top"
                                className="whitespace-normal w-[100px] xl:w-[200px]"
                                content="Revmatics automatically generated this Creator Brief from your data and other sources."
                              >
                                <FontAwesomeIcon
                                  icon={faStars}
                                  className="typography-body-md text-purple-600"
                                />
                              </RevTooltip>
                            )}
                          </div>
                        ))}
                      </div>
                    </RevPopoverMenu>
                  )}
                </div>
              ))}
            </div>
          );
        },
        enableSorting: true,
        size: 180,
      }),
      columnHelper.accessor("total_followers", {
        id: "totalFollowers",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-sm text-monochrome-900">
            Total Followers
          </span>
        ),
        cell: (info) => {
          const totalFollowers = info.getValue() as number;
          return (
            <span className="whitespace-nowrap typography-body-sm text-monochrome-900">
              {`${fullFormatNumbers({ value: totalFollowers, shortenValue: 1e3, decimalCount: 1 })}`}
            </span>
          );
        },
        enableSorting: true,
        size: 180,
      }),
      columnHelper.accessor("fit", {
        id: "fit",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-sm text-monochrome-900">
            Fit
          </span>
        ),
        cell: (info) => {
          const fit = info.getValue() as number;
          const fitStatus = getFitNameByNumber(fit);

          return fit ? (
            <RevStatusBadge
              text={fitStatus}
              variant="medium"
              color={getFitColor(fitStatus.toLowerCase())}
            />
          ) : (
            ""
          );
        },
        enableSorting: true,
        size: 180,
      }),
      columnHelper.accessor("health", {
        id: "health",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-sm text-monochrome-900">
            Health
          </span>
        ),
        cell: (info) => {
          const health = info.getValue() as number;
          const healthStatus = getFitNameByNumber(health);

          return health ? (
            <RevStatusBadge
              text={healthStatus}
              variant="medium"
              color={getFitColor(healthStatus.toLowerCase())}
            />
          ) : (
            ""
          );
        },
        enableSorting: true,
        size: 180,
      }),
      columnHelper.display({
        id: "actions",
        header: () => <span />,
        cell: (info) => {
          const creatorId = info.row.original.id;
          return (
            <div className="flex items-center gap-1 space-x-1 justify-center">
              <Menu>
                {({ open }) => {
                  const isBusy =
                    isLoadingLiked === creatorId ||
                    isLoadingDelete === creatorId;

                  const TheTrigger = isBusy ? (
                    <div className="px-3 py-1.5 flex items-center justify-center">
                      <RevSpinner color="current" size="medium" />
                    </div>
                  ) : (
                    <MenuButton
                      data-testid="more-actions"
                      as={RevActionButton}
                    />
                  );

                  return (
                    <div className="relative">
                      {!open && !isBusy ? (
                        <RevTooltip
                          content="More Actions"
                          position="left"
                          className="w-[90px]"
                        >
                          {TheTrigger}
                        </RevTooltip>
                      ) : (
                        TheTrigger
                      )}

                      <MenuItems
                        transition
                        anchor="bottom end"
                        className="w-52 z-20 shadow-elevation-1 bg-white origin-top-right rounded-md py-1 text-sm/6 transition duration-100 ease-out [--anchor-gap:--spacing(1)] focus:outline-none data-closed:scale-95 data-closed:opacity-0"
                      >
                        {activeTab === "creators_view" ? (
                          <>
                            <MenuItem>
                              <button
                                data-testid="creator-action-generate-brief-button"
                                onClick={() =>
                                  setIsCreateBriefDialogOpen(creatorId)
                                }
                                className="flex w-full items-center text-monochrome-900 gap-2 rounded-sm px-3 py-1.5 hover:bg-monochrome-50"
                              >
                                Generate Creator Brief
                              </button>
                            </MenuItem>
                            <MenuItem>
                              <button
                                data-testid="creator-action-remove-from-list-button"
                                onClick={() => {
                                  setIsLoadingDelete(creatorId);
                                  dispatch(deleteStatusCreator(creatorId)).then(
                                    () => setIsLoadingDelete(null),
                                  );
                                }}
                                className="flex w-full items-center text-monochrome-900 gap-2 rounded-sm px-3 py-1.5 hover:bg-monochrome-50"
                              >
                                Remove From this List
                              </button>
                            </MenuItem>
                          </>
                        ) : (
                          <>
                            <MenuItem>
                              <button
                                onClick={() => {
                                  setIsLoadingDelete(creatorId);
                                  dispatch(deleteStatusCreator(creatorId)).then(
                                    () => setIsLoadingDelete(null),
                                  );
                                }}
                                className="flex w-full items-center text-monochrome-900 gap-2 rounded-sm px-3 py-1.5 hover:bg-monochrome-50"
                              >
                                Unhide
                              </button>
                            </MenuItem>
                            <MenuItem>
                              <>
                                <button
                                  onClick={() => {
                                    setIsLoadingLiked(creatorId);
                                    dispatch(
                                      interactionsCreator({
                                        id: creatorId,
                                        body: { status: "liked" },
                                      }),
                                    ).then(() => setIsLoadingLiked(null));
                                  }}
                                  className="flex w-full items-center text-monochrome-900 gap-2 rounded-sm px-3 py-1.5 hover:bg-monochrome-50"
                                >
                                  Like
                                </button>
                              </>
                            </MenuItem>
                          </>
                        )}
                      </MenuItems>
                    </div>
                  );
                }}
              </Menu>
            </div>
          );
        },
        size: 120,
      }),
    ],
    [activeTab, dispatch, isLoadingDelete, isLoadingLiked],
  );

  if (isLoading && !displayCreators) {
    return (
      <div className="h-full p-4 sm:p-6 lg:p-8 text-center">
        <RevSpinner size="large" color="primary" centered />
      </div>
    );
  }

  if (!isLoading && !displayCreators) {
    return (
      <RevEmptyPage
        image={campaignEmpty}
        title="You have no liked or hidden creators."
        to="/personas"
        textBtn="Back to Personas"
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <RevActionsHeader title="Manage Creators" headerClassName="!mb-0" />
      <div className="flex justify-between gap-3">
        <RevTabs
          data={manageCreatorsTabsData}
          selected={activeTab}
          handleSelect={setActiveTab}
          tabId="creators-tabs"
          size="medium"
        />
        <RevButton
          text="Download"
          icon={faDownload}
          variant="secondary"
          isLoading={isLoadingDownload}
          handleClick={handleDownload}
        />
      </div>

      <RevTable<ManageCreatorData>
        data={displayCreators || []}
        columns={columns}
        initialPageSize={5}
        enableGlobalFilter={false}
        enablePagination={true}
        cellNoPadding={["platform", "handle"]}
        columnPinning={columnPinning}
        onColumnPinningChange={setColumnPinning}
      />
      <RevLandingPagePreviewDialog
        link={previewLink}
        onClose={() => setPreviewLink(null)}
      />
      {isCreateBriefDialogOpen && (
        <CreateBriefDialog
          isOpen={!!isCreateBriefDialogOpen}
          setIsOpen={() => setIsCreateBriefDialogOpen(null)}
          onSuccess={(brief) => dispatch(setBriefForCreator(brief))}
          initialCreatorId={isCreateBriefDialogOpen}
        />
      )}
    </div>
  );
};
